<template>
  <div v-if="offer">
    <v-btn
      :to="'/events/' + $route.params.id"
      class="ma-0 mb-5 mr-5"
      transparent
    >
      Назад
    </v-btn>
    <v-btn
      v-show="!offer.accepted"
      class="ma-0 mb-5 error"
      @click="deleteTrainerOffer()"
    >
      Удалить
    </v-btn>
    <h1 class="mb-5">
      Оффер для: {{ offer.trainer_name }}
    </h1>

    <!-- Итоговая выплата — всегда видна -->
    <v-card class="mb-4" color="grey lighten-4">
      <v-card-title class="pb-2">
        Итоговая выплата тренеру
      </v-card-title>
      <v-card-text>
        <v-simple-table dense class="payout-table">
          <tbody>
            <tr v-if="computedBasePayout != null">
              <td>
                Базовая выплата
                <span class="grey--text text--darken-1">
                  ({{ formatRub(effectiveRate) }} × {{ effectiveDays }} дн.)
                </span>
              </td>
              <td class="text-right"><b>{{ formatRub(computedBasePayout) }}</b></td>
            </tr>
            <tr v-if="computedBonusPayout != null">
              <td>
                Бонус за привлечённых участников
                <span v-if="selectedPromoName" class="grey--text text--darken-1">
                  (промокод «{{ selectedPromoName }}»: {{ formatRub(effectivePerParticipant) }} × {{ effectiveManDays }} чел-дней)
                </span>
              </td>
              <td class="text-right"><b>{{ formatRub(computedBonusPayout) }}</b></td>
            </tr>
            <tr
              v-for="(extra, index) in computedExtraPayouts"
              :key="'extra-preview-' + index"
            >
              <td>
                Доп. выплата
                <span v-if="extra.description" class="grey--text text--darken-1">({{ extra.description }})</span>
              </td>
              <td class="text-right"><b>{{ formatRub(extra.amount) }}</b></td>
            </tr>
            <tr v-if="computedTotalPayout != null" class="total-row">
              <td><b>Итого к выплате</b></td>
              <td class="text-right"><b class="total-amount">{{ formatRub(computedTotalPayout) }}</b></td>
            </tr>
            <tr v-if="computedTotalPayout == null">
              <td colspan="2" class="grey--text">
                Укажите сумму в день, бонус за привлечённых или дополнительные выплаты — расчёт появится автоматически.
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-card id="account-setting-card" class="mb-4">
      <v-card-title>Оффер</v-card-title>
      <v-card-text>
        <p>
          Здравствуйте, {{ offer.trainer_name }}!<br />
          Вам направлено предложение принять участие на мероприятии в качестве тренера / члена команды организаторов.
        </p>

        <p>
          Ниже представлены подробные данные и условия сотрудничества. <br />
          Название мероприятия: <b>{{ offer.event_title }}</b> <br />
          Даты работы: <b>{{ effectiveStartDateDisplay }} - {{ effectiveEndDateDisplay }}</b> <br />
          Расчетное количество рабочих дней: <b>{{ effectiveDays }}</b> <br />
        </p>
        <p>
          Согласованный функционал: <br />
          <span style="white-space: pre-line">{{ offer.agreed_functionality }}</span>
        </p>
        <p>
          Условия оплаты: <br />
          <span style="white-space: pre-line">{{ offer.salary_description }}</span>
        </p>
      </v-card-text>
    </v-card>

    <!-- Параметры выплат и промокод -->
    <v-card class="mb-4">
      <v-card-title>Параметры выплат</v-card-title>
      <v-card-text>
        <v-subheader class="pl-0">
          Даты работы
        </v-subheader>
        <v-row>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="edit_start_date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="edit_start_date_display"
                  label="Дата начала работы"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="edit_start_date"
                locale="ru"
                @input="edit_start_date_menu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="edit_end_date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="edit_end_date_display"
                  label="Дата окончания работы"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="edit_end_date"
                locale="ru"
                :min="edit_start_date"
                @input="edit_end_date_menu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <p v-if="effectiveDays != null" class="caption grey--text mb-4">
          Расчёт: {{ effectiveDays }} {{ effectiveDaysLabel }}
        </p>

        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="edit_rate_per_day"
              label="Сумма в день (₽)"
              type="number"
              min="0"
              hint="Базовая ставка тренера за каждый рабочий день"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="edit_per_participant_per_day"
              label="Сумма за привлечённого в день (₽)"
              type="number"
              min="0"
              hint="Бонус за каждый человеко-день по промокоду"
              persistent-hint
            ></v-text-field>
          </v-col>
        </v-row>

        <v-select
          v-model="edit_promo_code_id"
          :items="promo_codes_list"
          item-text="name"
          item-value="id"
          label="Промокод тренера"
          clearable
          class="mt-2"
          :hint="selectedPromoHint"
          persistent-hint
        ></v-select>

        <v-subheader class="pl-0 mt-4">
          Дополнительные выплаты
        </v-subheader>
        <p class="caption grey--text mb-2">
          Разовые доплаты, согласованные отдельно с тренером (трансфер, подготовка, и т.п.)
        </p>
        <div
          v-for="(extra, index) in edit_extra_payouts"
          :key="'extra-edit-' + index"
          class="mb-2"
        >
          <v-row dense align="center">
            <v-col cols="12" sm="7">
              <v-text-field
                v-model="extra.description"
                label="За что"
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model.number="extra.amount"
                label="Сумма (₽)"
                type="number"
                min="0"
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn
                small
                text
                color="error"
                @click="removeExtraPayout(index)"
              >
                Удалить
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <v-btn
          small
          color="secondary"
          class="mb-4"
          @click="addExtraPayout"
        >
          + Добавить доп
        </v-btn>

        <div class="mt-2">
          <v-btn
            color="primary"
            :loading="payout_saving"
            :disabled="payout_saving || !payoutChanged"
            @click="savePayoutSettings"
          >
            Сохранить параметры
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Блок промокода с человеко-днями -->
    <v-card
      v-if="edit_promo_code_id && effectiveManDaysPreviewAvailable"
      class="mb-4"
    >
      <v-card-title>
        Промокод «{{ selectedPromoName }}» — привлечённые участники
      </v-card-title>
      <v-card-text>
        <div class="mb-2">
          <span class="mr-4">Применений: <b>{{ effectiveBookingsCount }}</b></span>
          <span>Человеко-дней: <b>{{ effectiveManDays }}</b></span>
          <span v-if="computedBonusPayout != null" class="ml-4">
            Бонус: <b>{{ formatRub(computedBonusPayout) }}</b>
          </span>
        </div>
        <v-simple-table
          v-if="effectiveParticipants.length > 0"
          dense
        >
          <thead>
            <tr>
              <th class="text-left">Участник</th>
              <th class="text-left">Бронь #</th>
              <th class="text-right">Начало</th>
              <th class="text-right">Конец</th>
              <th class="text-right">Дней</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in effectiveParticipants"
              :key="p.id"
            >
              <td>{{ p.name }}</td>
              <td>{{ p.booking_id }}</td>
              <td class="text-right">{{ p.start }}</td>
              <td class="text-right">{{ p.end }}</td>
              <td class="text-right">{{ p.days }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th colspan="4">Итого человеко-дней</th>
              <th class="text-right">{{ effectiveManDays }}</th>
            </tr>
          </tfoot>
        </v-simple-table>
        <div v-else class="grey--text">
          Бронирований с этим промокодом пока нет — бонус будет 0 ₽.
        </div>
      </v-card-text>
    </v-card>

    <v-card id="account-setting-card">
      <v-card-title>Статус отправки</v-card-title>
      <v-card-text>
        <template v-if="offer.sent">
          Отправлено
        </template>
        <template v-else>
          <v-btn
            small
            color="primary"
            @click="sendOffer(offer.uuid)"
          >
            Отправлен
          </v-btn>
        </template>
      </v-card-text>
      <v-card-title>Статус ответа</v-card-title>
      <v-card-text>
        <template v-if="offer.answered">
          <template v-if="offer.accepted">
            Согласовано
          </template>
          <template v-else>
            Отказ
          </template>
        </template>
        <template v-else>
          Ответ не получен
        </template>
      </v-card-text>
      <v-card-title>Ссылка</v-card-title>
      <v-card-text> https://admin.khimkibc.ru/offer/{{ offer.uuid }}/ </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Api from '@/api/api'

export default {
  data() {
    return {
      offer: null,
      promo_codes_list: [],
      reservationList: [],
      edit_promo_code_id: null,
      edit_rate_per_day: null,
      edit_per_participant_per_day: null,
      edit_extra_payouts: [],
      edit_start_date: null,
      edit_end_date: null,
      edit_start_date_menu: false,
      edit_end_date_menu: false,
      payout_saving: false,
    }
  },
  computed: {
    promocodeManDays() {
      const manDays = {}
      this.reservationList.forEach(reservation => {
        if (reservation.promocode && reservation.participant_man_days) {
          const code = reservation.promocode
          const total = reservation.participant_man_days.total_man_days || 0
          manDays[code] = (manDays[code] || 0) + total
        }
      })
      return manDays
    },
    promocodeParticipants() {
      const byCode = {}
      this.reservationList.forEach(reservation => {
        if (reservation.promocode && reservation.participant_man_days?.participants?.length) {
          const code = reservation.promocode
          if (!byCode[code]) byCode[code] = []
          byCode[code].push(...reservation.participant_man_days.participants)
        }
      })
      return byCode
    },
    promocodeBookingsCount() {
      const counts = {}
      this.reservationList.forEach(reservation => {
        if (reservation.promocode) {
          const code = reservation.promocode
          counts[code] = (counts[code] || 0) + 1
        }
      })
      return counts
    },
    selectedPromoName() {
      if (!this.edit_promo_code_id) return null
      const promo = this.promo_codes_list.find(p => p.id === this.edit_promo_code_id)
      return promo ? promo.name : this.offer?.promo_code_name
    },
    effectiveRate() {
      const v = this.normalizeNumber(this.edit_rate_per_day)
      return v != null ? v : this.normalizeNumber(this.offer?.rate_per_day)
    },
    effectivePerParticipant() {
      const v = this.normalizeNumber(this.edit_per_participant_per_day)
      return v != null ? v : this.normalizeNumber(this.offer?.per_participant_per_day)
    },
    effectiveDays() {
      return this.calcDaysBetween(this.edit_start_date, this.edit_end_date)
    },
    effectiveDaysLabel() {
      const days = this.effectiveDays
      if (days == null) return 'дней'
      const mod10 = days % 10
      const mod100 = days % 100
      if (mod10 === 1 && mod100 !== 11) return 'день'
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'дня'
      return 'дней'
    },
    effectiveStartDateDisplay() {
      return this.formatOfferDateForDisplay(this.edit_start_date) || this.offer?.start_date || '—'
    },
    effectiveEndDateDisplay() {
      return this.formatOfferDateForDisplay(this.edit_end_date) || this.offer?.end_date || '—'
    },
    edit_start_date_display() {
      return this.formatOfferDateForDisplay(this.edit_start_date)
    },
    edit_end_date_display() {
      return this.formatOfferDateForDisplay(this.edit_end_date)
    },
    effectiveManDays() {
      if (!this.edit_promo_code_id) return 0
      if (
        this.edit_promo_code_id === this.offer?.promo_code_id
        && this.offer?.promocode_stats
      ) {
        return this.offer.promocode_stats.total_man_days || 0
      }
      const name = this.selectedPromoName
      if (!name) return 0
      return this.promocodeManDays[name] || 0
    },
    effectiveParticipants() {
      if (
        this.edit_promo_code_id === this.offer?.promo_code_id
        && this.offer?.promocode_stats?.participants?.length
      ) {
        return this.offer.promocode_stats.participants
      }
      const name = this.selectedPromoName
      if (!name) return []
      return this.promocodeParticipants[name] || []
    },
    effectiveBookingsCount() {
      if (
        this.edit_promo_code_id === this.offer?.promo_code_id
        && this.offer?.promocode_stats
      ) {
        return this.offer.promocode_stats.bookings_count || 0
      }
      const name = this.selectedPromoName
      if (!name) return 0
      return this.promocodeBookingsCount[name] || 0
    },
    effectiveManDaysPreviewAvailable() {
      return this.selectedPromoName != null
    },
    computedBasePayout() {
      if (this.effectiveRate == null || this.effectiveDays == null) return null
      return this.effectiveRate * this.effectiveDays
    },
    computedBonusPayout() {
      if (this.effectivePerParticipant == null || !this.edit_promo_code_id) return null
      return this.effectivePerParticipant * this.effectiveManDays
    },
    computedExtraPayouts() {
      return this.sanitizeExtraPayouts(this.edit_extra_payouts)
    },
    computedExtrasTotal() {
      return this.computedExtraPayouts.reduce((sum, item) => sum + item.amount, 0)
    },
    computedTotalPayout() {
      const base = this.computedBasePayout
      const bonus = this.computedBonusPayout
      const hasExtras = this.computedExtraPayouts.length > 0
      if (base == null && bonus == null && !hasExtras) return null
      return (base || 0) + (bonus || 0) + this.computedExtrasTotal
    },
    payoutChanged() {
      if (!this.offer) return false
      return (
        this.offerStartDateChanged()
        || this.offerEndDateChanged()
        || (this.offer.promo_code_id ?? null) !== (this.edit_promo_code_id ?? null)
        || this.normalizeNumber(this.offer.rate_per_day) !== this.normalizeNumber(this.edit_rate_per_day)
        || this.normalizeNumber(this.offer.per_participant_per_day) !== this.normalizeNumber(this.edit_per_participant_per_day)
        || !this.extrasEqual(this.offer.extra_payouts, this.edit_extra_payouts)
      )
    },
    selectedPromoHint() {
      if (!this.edit_promo_code_id) return 'Выберите промокод для расчёта бонуса за привлечённых'
      const promo = this.promo_codes_list.find(p => p.id === this.edit_promo_code_id)
      if (!promo) return ''
      const manDays = this.effectiveManDays
      return `Скидка ${promo.discount}% · человеко-дней: ${manDays}`
    },
  },
  async created() {
    this.$watch(() => this.$route.params)
  },
  async mounted() {
    this.getPromocodesForEvent()
    this.loadReservations()
    this.getTrainerOffer()
  },
  methods: {
    normalizeNumber(value) {
      if (value === null || value === undefined || value === '') return null
      const n = Number(value)
      return Number.isFinite(n) ? n : null
    },
    parseOfferDateFromApi(value) {
      if (!value) return null
      const dotted = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
      if (dotted) {
        return `${dotted[3]}-${dotted[2]}-${dotted[1]}`
      }
      return value
    },
    formatOfferDateForDisplay(value) {
      if (!value) return ''
      const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (iso) {
        return `${iso[3]}.${iso[2]}.${iso[1]}`
      }
      return value
    },
    calcDaysBetween(start, end) {
      if (!start || !end) return null
      const startDate = new Date(start)
      const endDate = new Date(end)
      const diff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
      return diff >= 0 ? diff : null
    },
    savedOfferStartDateIso() {
      return this.parseOfferDateFromApi(this.offer?.start_date)
    },
    savedOfferEndDateIso() {
      return this.parseOfferDateFromApi(this.offer?.end_date)
    },
    offerStartDateChanged() {
      return (this.edit_start_date ?? null) !== (this.savedOfferStartDateIso() ?? null)
    },
    offerEndDateChanged() {
      return (this.edit_end_date ?? null) !== (this.savedOfferEndDateIso() ?? null)
    },
    syncEditDatesFromOffer(offer) {
      this.edit_start_date = this.parseOfferDateFromApi(offer.start_date)
      this.edit_end_date = this.parseOfferDateFromApi(offer.end_date)
    },
    formatRub(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString('ru-RU') + ' ₽'
    },
    sanitizeExtraPayouts(list) {
      return (list || [])
        .map(item => ({
          description: (item.description || '').trim(),
          amount: this.normalizeNumber(item.amount),
        }))
        .filter(item => item.amount != null)
    },
    extrasEqual(saved, edited) {
      return JSON.stringify(this.sanitizeExtraPayouts(saved))
        === JSON.stringify(this.sanitizeExtraPayouts(edited))
    },
    addExtraPayout() {
      this.edit_extra_payouts.push({ description: '', amount: null })
    },
    removeExtraPayout(index) {
      this.edit_extra_payouts.splice(index, 1)
    },
    cloneExtraPayouts(list) {
      return (list || []).map(item => ({
        description: item.description || '',
        amount: item.amount ?? null,
      }))
    },
    openOffer() { },
    sendOffer(uuid) {
      this.loader = true
      Api.sendTrainerOffer({
        uuid,
      }).then(event => {
        this.getTrainerOffer()
        this.loader = false
      })
    },
    getTrainerOffer() {
      const params = new URLSearchParams([['uuid', this.$route.params.uuid]])
      Api.getTrainerOffer({ params }).then(offer => {
        this.offer = offer
        this.edit_promo_code_id = offer.promo_code_id ?? null
        this.edit_rate_per_day = offer.rate_per_day ?? null
        this.edit_per_participant_per_day = offer.per_participant_per_day ?? null
        this.edit_extra_payouts = this.cloneExtraPayouts(offer.extra_payouts)
        this.syncEditDatesFromOffer(offer)
      })
    },
    loadReservations() {
      Api.getReservationListByEventId({
        event_id: parseInt(this.$route.params.id),
      }).then(data => {
        this.reservationList = Array.isArray(data) ? data : []
      })
    },
    getPromocodesForEvent() {
      Api.getPromocodes({ event_id: parseInt(this.$route.params.id) }).then(data => {
        this.promo_codes_list = Array.isArray(data) ? data : []
      })
    },
    savePayoutSettings() {
      this.payout_saving = true
      Api.editTrainerOffer({
        uuid: this.offer.uuid,
        start_date: this.edit_start_date || null,
        end_date: this.edit_end_date || null,
        promo_code_id: this.edit_promo_code_id || null,
        rate_per_day: this.normalizeNumber(this.edit_rate_per_day),
        per_participant_per_day: this.normalizeNumber(this.edit_per_participant_per_day),
        extra_payouts: this.sanitizeExtraPayouts(this.edit_extra_payouts),
      }).then(offer => {
        this.offer = offer
        this.edit_promo_code_id = offer.promo_code_id ?? null
        this.edit_rate_per_day = offer.rate_per_day ?? null
        this.edit_per_participant_per_day = offer.per_participant_per_day ?? null
        this.edit_extra_payouts = this.cloneExtraPayouts(offer.extra_payouts)
        this.syncEditDatesFromOffer(offer)
        this.payout_saving = false
      }).catch(() => {
        this.payout_saving = false
      })
    },
    deleteTrainerOffer() {
      const params = new URLSearchParams([['uuid', this.$route.params.uuid]])
      Api.deleteTrainerOfferAnswer({ params }).then(response => {
        this.$router.push({
          path: `/events/${this.$route.params.id}`,
          replace: true,
        })
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.payout-table .total-row td {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
  padding-top: 12px;
}
.total-amount {
  font-size: 1.25rem;
  color: #2e7d32;
}
</style>
